import React from 'react';

import { ArrowKeysTrap } from './KeyboardNavigation';

import { cn } from 'domain/core';
import { createId, noop } from 'domain/utils';
import { ButtonProps, DivProps } from './common-types';

type TabsCtx = {
	id: string;
	active: string;
	setActive(key: string): void;
};

const TabsContext = React.createContext<TabsCtx>({
	id: createId('tabs-'),
	active: '',
	setActive: noop,
});

const useTabsContext = () => {
	const { id, active, setActive } = React.useContext(TabsContext);

	return React.useMemo(() => ({ active, setActive, id }), [active, setActive]);
};

const TabsProvider = ({
	id,
	active,
	setActive,
	children,
}: React.PropsWithChildren<TabsCtx>) => {
	const ctx = React.useMemo(
		() => ({ id, active, setActive }),
		[active, setActive]
	);
	return <TabsContext.Provider value={ctx}>{children}</TabsContext.Provider>;
};

interface TabsProps extends DivProps {
	value: string;
	onTabChange(val: string): void;
}

/**
 * Tabs main component
 * @param param0
 * @returns
 */
const Tabs = ({
	value,
	onTabChange,
	children,
	id,
	className,
	...props
}: TabsProps) => {
	return (
		<TabsProvider
			id={id ?? createId('tabs-')}
			active={value}
			setActive={onTabChange}>
			<div
				tabIndex={-1}
				className={cn(
					'tabs',
					'nowrap flex flex-col items-center justify-start',
					className
				)}
				{...props}>
				{children}
			</div>
		</TabsProvider>
	);
};

interface ListProps extends DivProps {}
/**
 * Tabs item list
 * @param props
 * @returns
 */
const List = ({ className, children, ...props }: ListProps) => {
	return (
		<div
			className={cn(
				'tablist',
				'flex flex-row flex-nowrap items-center justify-center',
				'gap-2',
				className
			)}
			{...props}
			role='tablist'>
			<ArrowKeysTrap>{children}</ArrowKeysTrap>
		</div>
	);
};

interface TabProps extends ButtonProps {
	value: string;
}
/**
 * Tab item
 * @param props
 * @returns
 */
const Tab = (props: TabProps) => {
	const { id, active, setActive } = useTabsContext();
	const { value, className, children, onClick, disabled, ...rest } = props;

	const intOnClick = React.useCallback(
		(evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			setActive(value);
			if (onClick) {
				onClick(evt);
			}
		},
		[setActive, onClick, value]
	);

	return (
		<button
			className={cn(
				'tab',
				'nowrap flex flex-row items-end justify-center',
				'gap-4 px-4 py-2',
				'rounded-tr rounded-tl border-b-2 border-neutral-100 bg-neutral-100',
				'transition-colors',
				!disabled && 'hover:border-primary-300 hover:bg-neutral-200',
				active === value && '!border-primary-700 !bg-primary-100',
				disabled && 'text-neutral-500',
				className
			)}
			type='button'
			aria-selected={active === value}
			aria-controls={`${id}-panel-${value}`}
			id={`${id}-tab-${value}`}
			onClick={intOnClick}
			tabIndex={active === value ? 0 : -1}
			disabled={disabled}
			{...rest}>
			{children}
		</button>
	);
};

interface PanelProps extends DivProps {
	value: string;
}
/**
 * Tab panel. Displayed automatically when the `active` property of Tabs matches this component's `value`.
 */
const Panel = ({ children, value, ...props }: PanelProps) => {
	const { id, active } = useTabsContext();

	if (React.Children.count(children) === 1) {
		const child = React.Children.only(children);
		if (React.isValidElement(child) && active === value) {
			return React.cloneElement<any>(child, {
				...props,
				role: 'tabpanel',
				id: `${id}-panel-${value}`,
				'aria-labelledby': `${id}-tab-${value}`,
				tabIndex: 0,
			});
		}
	}

	return null;
};

Tabs.List = List;
Tabs.Tab = Tab;
Tabs.Panel = Panel;

Tabs.displayName = 'Tabs';

export { Tabs };
